import './styles.scss';

import { useEffect, useRef, useState } from 'react';

import Button from '@/common/Button';
import Modal from '@/common/Modal';
import Textarea from '@/common/Textarea';
import Toast from '@/models/Toast';
import { createNotification } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';

export type PickupInstructionsModalProps = {
  show: boolean;
  initialValue?: string;
  onHide: () => void;
  onSubmit: (value: string) => void;
};

/**
 * PickupInstructionsModal
 *
 * A modal for entering detailed pick-up location directions for a pickup point.
 * Displays a textarea pre-filled with the current instructions value.
 * Calls `onSubmit` with the textarea value when the user clicks Save.
 */
const PickupInstructionsModal = ({ show, initialValue = '', onHide, onSubmit }: PickupInstructionsModalProps): JSX.Element => {
  const [value, setValue] = useState<string>(initialValue);
  const textAreaInputRef = useRef(null);

  useEffect((): void => {
    if (show) {
      setValue(initialValue);
      textAreaInputRef.current?.focus();
    }
  }, [show, initialValue]);

  const areYouSureConfirmation = useConfirmation({
    Body: { message: 'You have unsaved changes. Are you sure you want to leave?' },
  });

  const handleHide = async () => {
    const trimmed = value.trim();
    if (trimmed !== initialValue) {
      try {
        await areYouSureConfirmation();
      } catch {
        return;
      }
    }
    onHide();
  };

  const handleSubmit = (): void => {
    const trimmed = value.trim();
    if (!trimmed.length) {
      createNotification('Please enter instructions for the pick-up location finder.', Toast.Type.WARNING, 'Pickup Instructions');
      return;
    }
    if (trimmed === initialValue) {
      onHide();
      return;
    }
    onSubmit(trimmed);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      title="PICK-UP LOCATION INSTRUCTIONS"
      size="sm"
      centered
      footer={(): JSX.Element => (
        <>
          <Button name="CANCEL" variant="secondary" onClick={handleHide}>
            Cancel
          </Button>
          <Button name="SUBMIT" variant="primary" onClick={handleSubmit} disabled={!value.length || value.trim() === initialValue}>
            Save
          </Button>
        </>
      )}
    >
      <div className="PickupInstructionsModal">
        <Textarea
          name="instructions"
          placeholder="Enter detailed instructions for the pick-up location finder"
          value={value}
          onChange={(value): void => setValue(value as unknown as string)}
          rows={5}
          ref={textAreaInputRef}
        />
      </div>
    </Modal>
  );
};

export default PickupInstructionsModal;
